/**
 * @module agent/adapters/services/SearchPollCoordinator
 *
 * Coordinates search polling lifecycle - manages timeouts and state transitions.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Uses SearchWorkflowService for pure state machine logic.
 * This class handles the impure runtime concerns: timeouts, callbacks.
 *
 * TD-2952: holds a Map of in-flight searches keyed by searchId so successive
 * searches keep independent poll timers instead of abandoning the first.
 */

import {
  createInitialState,
  startSearch,
  handleTimeout,
  incrementPollAttempt,
  isActive,
  isTimedOut,
  getSearchDuration,
  parseSearchTags,
  createPollMessage,
  SEARCH_CONFIG,
  SEARCH_PROGRESS_PHASES,
  SEARCH_TAG_REASONS
} from './SearchWorkflowService';

/**
 * Coordinates search polling with timeout management.
 * Supports multiple concurrent in-flight searches (one entry per searchId).
 */
class SearchPollCoordinator {
  /**
   * @param {Object} callbacks - Callback functions for adapter integration
   * @param {Function} callbacks.onStateChange - Called with state name ('searching', 'idle')
   * @param {Function} callbacks.sendHiddenMessage - Async function to send hidden poll message
   * @throws {TypeError} If callbacks is not an object or callback types are invalid
   */
  constructor(callbacks) {
    // Fix #1: Validate callbacks
    if (!callbacks || typeof callbacks !== 'object') {
      throw new TypeError('SearchPollCoordinator: callbacks must be an object');
    }
    if (callbacks.onStateChange && typeof callbacks.onStateChange !== 'function') {
      throw new TypeError('SearchPollCoordinator: callbacks.onStateChange must be a function');
    }
    if (callbacks.sendHiddenMessage && typeof callbacks.sendHiddenMessage !== 'function') {
      throw new TypeError('SearchPollCoordinator: callbacks.sendHiddenMessage must be a function');
    }

    this._callbacks = callbacks;
    /** @type {Map<string, {state: Object, pollTimeout: ReturnType<typeof setTimeout>|null, consecutiveErrors: number, completionPending: boolean, pollUnanswered: boolean, seq: number}>} */
    this._searches = new Map();
    this._startSeq = 0;
    // Stable inactive reference so getState() is referentially equal when idle
    this._inactiveState = Object.freeze(createInitialState());
    this._isDestroyed = false; // Fix #7: Track destroyed state for lifecycle safety
  }

  /**
   * Emit a search progress event for a specific search entry.
   * @param {Object} entry - Search map entry
   * @param {string} phase - One of SEARCH_PROGRESS_PHASES
   * @param {number} targetProgress - Target progress percentage (0-100)
   * @private
   */
  _emitProgress(entry, phase, targetProgress) {
    // TD-1904: Include sessionId so progress is tracked per-session
    this._callbacks?.onSearchProgress?.(entry.state.sessionId, { phase, targetProgress });
  }

  /**
   * Emit progress for a BUSY poll response based on current poll count.
   * @param {Object} entry - Search map entry
   * @private
   */
  _emitBusyProgress(entry) {
    const { pollAttempts } = entry.state;
    if (pollAttempts <= 1) {
      this._emitProgress(entry, SEARCH_PROGRESS_PHASES.EVALUATING, 40);
    } else {
      const maxPolls = SEARCH_CONFIG.MAX_POLL_ATTEMPTS;
      const ratio = Math.min((pollAttempts - 1) / (maxPolls - 1), 1);
      const target = 65 + ratio * 20; // 65% → 85%
      this._emitProgress(entry, SEARCH_PROGRESS_PHASES.RATIONALE, Math.round(target));
    }
  }

  /**
   * Check if coordinator has been destroyed.
   * Fix #7: Provides explicit lifecycle state for safe external checks.
   * @returns {boolean} True if destroy() has been called
   */
  get isDestroyed() {
    return this._isDestroyed;
  }

  /**
   * Return active search entries, optionally filtered by session.
   * @param {string} [sessionId]
   * @returns {Array<{state: Object, pollTimeout: *, consecutiveErrors: number, completionPending: boolean}>}
   * @private
   */
  _activeEntries(sessionId) {
    const entries = [];
    for (const entry of this._searches.values()) {
      if (!isActive(entry.state)) continue;
      if (sessionId != null && entry.state.sessionId !== sessionId) continue;
      entries.push(entry);
    }
    return entries;
  }

  /**
   * Most recently started active search (highest startTime), optionally session-scoped.
   * @param {string} [sessionId]
   * @returns {Object} SearchState (inactive initial state when none)
   * @private
   */
  _latestActiveState(sessionId) {
    const entries = this._activeEntries(sessionId);
    if (entries.length === 0) {
      return this._inactiveState;
    }
    // Prefer highest startTime; break ties with insertion seq (fake timers share Date.now())
    entries.sort((a, b) => {
      const timeDiff = (b.state.startTime ?? 0) - (a.state.startTime ?? 0);
      if (timeDiff !== 0) return timeDiff;
      return (b.seq ?? 0) - (a.seq ?? 0);
    });
    return entries[0].state;
  }

  /**
   * Resolve which search a BUSY/completion response belongs to.
   * Prefers pollSearchId from the outbound CHECK_SEARCH_RESULTS message;
   * falls back to the sole active search for the session.
   * @param {string} sessionId
   * @param {{pollSearchId?: string}|null|undefined} options
   * @returns {Object|null} Map entry or null when ambiguous / missing
   * @private
   */
  _resolveEntryForResponse(sessionId, options) {
    const pollSearchId = options?.pollSearchId;
    if (pollSearchId) {
      const entry = this._searches.get(pollSearchId);
      if (entry && isActive(entry.state) && entry.state.sessionId === sessionId) {
        return entry;
      }
      return null;
    }

    const entries = this._activeEntries(sessionId);
    if (entries.length === 1) {
      return entries[0];
    }
    return null;
  }

  /**
   * Get current search state (latest active overall — for external inspection).
   * Fix #7: Throws if coordinator is destroyed - caller should check isDestroyed first.
   * @returns {Object} Current search state
   * @throws {Error} If called after destroy()
   */
  getState() {
    if (this._isDestroyed) {
      throw new Error('SearchPollCoordinator: Cannot call getState() after destroy()');
    }
    return this._latestActiveState();
  }

  /**
   * Get the most recently started active search for a specific session.
   * @param {string} sessionId
   * @returns {Object} SearchState (inactive when none for that session)
   * @throws {Error} If called after destroy()
   */
  getStateForSession(sessionId) {
    if (this._isDestroyed) {
      throw new Error('SearchPollCoordinator: Cannot call getStateForSession() after destroy()');
    }
    return this._latestActiveState(sessionId);
  }

  /**
   * Check if any search is currently active.
   * Fix #7: Returns false if destroyed - safe for conditional checks.
   * @returns {boolean} True if search is active, false if destroyed
   */
  isSearchActive() {
    if (this._isDestroyed) {
      return false;
    }
    return this._activeEntries().length > 0;
  }

  /**
   * Check if a search is currently active for a specific session.
   * @param {string} sessionId - Session ID to check
   * @returns {boolean} True if any search is active for this session
   */
  isSearchActiveForSession(sessionId) {
    if (this._isDestroyed) {
      return false;
    }
    return this._activeEntries(sessionId).length > 0;
  }

  /**
   * True only while a search for this session is genuinely still awaiting results
   * (polling) — NOT during the ~300ms post-completion window where results have
   * already been surfaced but the state reset is still pending. TD-2383: gates the
   * Stop control's "Stopped by user" acknowledgement so it never doubles up with
   * real results that arrived a moment before the click.
   * @param {string} sessionId - Session ID to check
   * @returns {boolean} True if a search for this session is still awaiting results
   */
  isAwaitingResults(sessionId) {
    if (this._isDestroyed) {
      return false;
    }
    return this._activeEntries(sessionId).some((entry) => !entry.completionPending);
  }

  /**
   * True when the last poll sent for this search got neither a [BUSY] nor its results.
   * The adapter checks this once the poll turn has finished: such a turn (an agent
   * `error` event, an empty reply) would otherwise leave the search active with no
   * poll scheduled, so the chat stays on "Searching…" until the user presses Stop.
   * @param {string} searchId
   * @returns {boolean}
   */
  isPollUnanswered(searchId) {
    if (this._isDestroyed) {
      return false;
    }
    const entry = this._searches.get(searchId);
    return Boolean(entry && isActive(entry.state) && !entry.completionPending && entry.pollUnanswered);
  }

  /**
   * Handle search tags in response content.
   * Parses content for search control tags and triggers appropriate actions.
   *
   * @param {string} content - Response content to check
   * @param {string} sessionId - Session ID for context
   * @param {{pollSearchId?: string}} [options] - Optional poll correlation from outbound CHECK_SEARCH_RESULTS
   * @returns {Object} { isInternalOnly: boolean, reason: string }
   */
  handleSearchTags(content, sessionId, options = {}) {
    // Fix #7: Safe return when destroyed
    if (this._isDestroyed) {
      return { isInternalOnly: false, reason: SEARCH_TAG_REASONS.INVALID_CONTENT };
    }

    if (!content || typeof content !== 'string') {
      return { isInternalOnly: false, reason: SEARCH_TAG_REASONS.INVALID_CONTENT };
    }

    if (import.meta.env.DEV) {
      console.log('🔍 SearchPollCoordinator: Checking for search tags');
    }

    const tags = parseSearchTags(content);

    // 1. Search initiation: [SEARCH_STARTED:tool_name:searchId]
    if (tags.hasSearchStart && tags.toolName && tags.searchId) {
      if (import.meta.env.DEV) console.log('🔍 SearchPollCoordinator: Search initiation detected:', {
        toolName: tags.toolName,
        searchId: tags.searchId
      });
      this._handleSearchInitiation(tags.toolName, tags.searchId, sessionId);
      return { isInternalOnly: true, reason: SEARCH_TAG_REASONS.SEARCH_INITIATION };
    }

    // 2. Busy response: [BUSY]
    if (tags.hasBusy) {
      if (import.meta.env.DEV) console.log('⏳ SearchPollCoordinator: Agent busy with search');
      const entry = this._resolveEntryForResponse(sessionId, options);
      if (!entry) {
        // Active searches exist for other sessions, or ambiguous multi-search without poll id
        if (this._activeEntries().length > 0 && this._activeEntries(sessionId).length === 0) {
          if (import.meta.env.DEV) console.warn('⚠️ SearchPollCoordinator: BUSY from different session, ignoring:', {
            received: sessionId
          });
          return { isInternalOnly: true, reason: SEARCH_TAG_REASONS.BUSY_WRONG_SESSION };
        }
        return { isInternalOnly: true, reason: SEARCH_TAG_REASONS.BUSY_RESPONSE };
      }

      entry.pollUnanswered = false;
      this._emitBusyProgress(entry);
      this._scheduleNextPoll(entry.state.searchId, entry.state.sessionId);
      return { isInternalOnly: true, reason: SEARCH_TAG_REASONS.BUSY_RESPONSE };
    }

    // 3. Search completion: No tags + resolvable active search = final results
    const completeEntry = this._resolveEntryForResponse(sessionId, options);
    if (completeEntry) {
      if (import.meta.env.DEV) console.log('✅ SearchPollCoordinator: Search completed');
      this._handleSearchCompletion(completeEntry.state.searchId);
      return { isInternalOnly: false, reason: SEARCH_TAG_REASONS.SEARCH_COMPLETED };
    }

    // 4. Normal response (including ambiguous multi-search without pollSearchId)
    return { isInternalOnly: false, reason: SEARCH_TAG_REASONS.NORMAL_RESPONSE };
  }

  /**
   * Handle search initiation - add/replace this searchId and start its poll loop.
   * Does not stop other in-flight searches (TD-2952).
   * @private
   */
  _handleSearchInitiation(toolName, searchId, sessionId) {
    const existingOthers = this._activeEntries().filter((e) => e.state.searchId !== searchId);
    if (existingOthers.length > 0 && import.meta.env.DEV) {
      console.warn('⚠️ SearchPollCoordinator: Starting new search while others are active:', {
        active: existingOthers.map((e) => ({
          searchId: e.state.searchId,
          sessionId: e.state.sessionId
        })),
        new: { searchId, sessionId }
      });
    }

    // Replacing the same searchId (retry) — clear its previous timer only
    const prior = this._searches.get(searchId);
    if (prior) {
      this._stopPolling(searchId);
    }

    const state = startSearch(createInitialState(), { toolName, searchId, sessionId });
    this._searches.set(searchId, {
      state,
      pollTimeout: null,
      consecutiveErrors: 0,
      completionPending: false,
      pollUnanswered: false,
      seq: ++this._startSeq
    });

    if (import.meta.env.DEV) console.log('🔍 SearchPollCoordinator: Setting state to SEARCHING');
    this._callbacks.onStateChange?.(sessionId, 'searching');
    this._emitProgress(this._searches.get(searchId), SEARCH_PROGRESS_PHASES.INITIATING, 0);

    this._startSearchPolling(searchId, sessionId);
  }

  /**
   * Start polling mechanism for one search.
   * @private
   */
  _startSearchPolling(searchId, sessionId) {
    this._stopPolling(searchId);
    const entry = this._searches.get(searchId);
    if (!entry) return;
    this._emitProgress(entry, SEARCH_PROGRESS_PHASES.ANALYZING, 15);
    this._scheduleNextPoll(searchId, sessionId);
    if (import.meta.env.DEV) console.log('⏰ SearchPollCoordinator: Polling started', searchId);
  }

  /**
   * Schedule next poll check for one search.
   * @private
   */
  _scheduleNextPoll(searchId, sessionId) {
    const entry = this._searches.get(searchId);
    if (!entry || !isActive(entry.state)) {
      return;
    }

    entry.state = incrementPollAttempt(entry.state);

    if (isTimedOut(entry.state)) {
      if (import.meta.env.DEV) console.warn(`⏰ SearchPollCoordinator: Timeout after ${entry.state.pollAttempts} attempts`);
      this._handleSearchTimeout(searchId);
      return;
    }

    const { pollAttempts } = entry.state;
    if (import.meta.env.DEV) console.log(
      `🔄 SearchPollCoordinator: Poll ${pollAttempts}/${SEARCH_CONFIG.MAX_POLL_ATTEMPTS} (${searchId})`
    );

    entry.pollTimeout = setTimeout(async () => {
      const current = this._searches.get(searchId);
      if (!current || !isActive(current.state)) {
        return;
      }

      current.pollUnanswered = true;
      try {
        if (import.meta.env.DEV) console.log('🔄 SearchPollCoordinator: Sending poll message', searchId);
        await this._callbacks.sendHiddenMessage?.(sessionId, createPollMessage(searchId));
        const after = this._searches.get(searchId);
        if (after) after.consecutiveErrors = 0;
      } catch (error) {
        console.error('❌ SearchPollCoordinator: Poll error:', error);
        this._handlePollError(searchId, sessionId);
      }
    }, SEARCH_CONFIG.POLL_INTERVAL_MS);
  }

  /**
   * Stop polling and clear timeout for one search (or no-op if missing).
   * @param {string} searchId
   * @private
   */
  _stopPolling(searchId) {
    const entry = this._searches.get(searchId);
    if (entry?.pollTimeout) {
      clearTimeout(entry.pollTimeout);
      entry.pollTimeout = null;
    }
  }

  /**
   * Retry poll directly after backoff (Fix #2).
   * @private
   */
  async _retryPoll(searchId, sessionId) {
    const entry = this._searches.get(searchId);
    if (!entry || !isActive(entry.state)) {
      return;
    }

    entry.pollUnanswered = true;
    try {
      if (import.meta.env.DEV) console.log('🔄 SearchPollCoordinator: Sending poll message (retry)', searchId);
      await this._callbacks.sendHiddenMessage?.(sessionId, createPollMessage(searchId));
      const after = this._searches.get(searchId);
      if (after) after.consecutiveErrors = 0;
    } catch (error) {
      console.error('❌ SearchPollCoordinator: Poll retry error:', error);
      this._handlePollError(searchId, sessionId);
    }
  }

  /**
   * Handle a poll error with consecutive error tracking and exponential backoff.
   * @private
   */
  _handlePollError(searchId, sessionId) {
    const entry = this._searches.get(searchId);
    // Results already arrived: the 300ms completion timer owns the entry now.
    if (!entry || entry.completionPending) return;

    entry.consecutiveErrors++;

    if (entry.consecutiveErrors >= 3) {
      console.error('❌ SearchPollCoordinator: Max consecutive errors reached, stopping search');
      this._handleSearchTimeout(searchId);
      return;
    }

    if (isActive(entry.state)) {
      const backoffMs = Math.min(
        SEARCH_CONFIG.POLL_INTERVAL_MS * Math.pow(2, entry.consecutiveErrors - 1),
        60000
      );
      if (import.meta.env.DEV) console.log(
        `🔄 SearchPollCoordinator: Retrying in ${backoffMs / 1000}s (error ${entry.consecutiveErrors}/3)`
      );
      entry.pollTimeout = setTimeout(() => this._retryPoll(searchId, sessionId), backoffMs);
    }
  }

  /**
   * Notify idle only when this session has no remaining active searches.
   * @param {string} sessionId
   * @private
   */
  _notifyIdleIfSessionClear(sessionId) {
    if (!sessionId) return;
    if (this._activeEntries(sessionId).length === 0) {
      this._callbacks?.onStateChange?.(sessionId, 'idle');
    }
  }

  /**
   * Handle search completion for one searchId.
   * @param {string} searchId
   * @private
   */
  _handleSearchCompletion(searchId) {
    const entry = this._searches.get(searchId);
    if (!entry) return;

    this._stopPolling(searchId);
    entry.pollUnanswered = false;
    // TD-2383: results are in — from here the Stop control must not add a
    // "Stopped by user" note (see isAwaitingResults). Reset once state clears below.
    entry.completionPending = true;

    const duration = getSearchDuration(entry.state);
    if (import.meta.env.DEV) console.log(`🎉 SearchPollCoordinator: Completed in ${duration.toFixed(1)}s`, searchId);

    this._emitProgress(entry, SEARCH_PROGRESS_PHASES.COMPLETE, 100);

    // Brief UX delay so the progress bar visually reaches 100% before results replace it.
    // State reset is deferred into the timeout so isSearchActive() remains true during
    // the delay — this prevents AgentCoreAdapter from firing an immediate 'idle' notification
    // when streaming finishes before the 300ms window expires.
    const completedSessionId = entry.state.sessionId;
    entry.pollTimeout = setTimeout(() => {
      if (this._isDestroyed) return;
      this._searches.delete(searchId);
      // Only idle when no other searches remain for this session
      this._notifyIdleIfSessionClear(completedSessionId);
    }, 300);
  }

  /**
   * Handle search timeout for one searchId.
   * @param {string} searchId
   * @private
   */
  _handleSearchTimeout(searchId) {
    const entry = this._searches.get(searchId);
    if (!entry) return;

    this._stopPolling(searchId);
    entry.completionPending = false;

    const duration = getSearchDuration(entry.state);
    if (import.meta.env.DEV) console.warn(`⏰ SearchPollCoordinator: Timed out after ${duration.toFixed(1)}s`, searchId);

    this._emitProgress(entry, SEARCH_PROGRESS_PHASES.COMPLETE, 100);
    const timedOutSessionId = entry.state.sessionId;
    // Mark inactive then remove — handleTimeout returns fresh inactive state
    entry.state = handleTimeout(entry.state);
    this._searches.delete(searchId);
    this._notifyIdleIfSessionClear(timedOutSessionId);
  }

  /**
   * Force stop all active searches.
   * Used during cleanup/destroy.
   * Fix #7: Safe to call when destroyed - no-op.
   */
  stop() {
    if (this._isDestroyed) {
      return;
    }
    for (const searchId of [...this._searches.keys()]) {
      this._stopPolling(searchId);
      this._searches.delete(searchId);
    }
  }

  /**
   * Session-aware stop for the Stop control (TD-2347).
   * Stops every in-flight search belonging to the given session and notifies idle.
   * @param {string} sessionId - Session whose in-flight searches should be stopped
   * @returns {boolean} True if at least one search for this session was stopped
   */
  stopForSession(sessionId) {
    if (this._isDestroyed) {
      return false;
    }
    const toStop = this._activeEntries(sessionId);
    if (toStop.length === 0) {
      return false;
    }
    for (const entry of toStop) {
      const searchId = entry.state.searchId;
      this._stopPolling(searchId);
      this._searches.delete(searchId);
    }
    this._callbacks?.onStateChange?.(sessionId, 'idle');
    return true;
  }

  /**
   * Clean up resources.
   * Fix #7: Sets _isDestroyed flag and clears state.
   * Safe to call multiple times.
   */
  destroy() {
    if (this._isDestroyed) {
      return; // Already destroyed, no-op
    }
    for (const searchId of [...this._searches.keys()]) {
      this._stopPolling(searchId);
    }
    this._searches.clear();
    this._callbacks = null;
    this._isDestroyed = true; // Fix #7: Mark as destroyed AFTER cleanup
  }
}

export default SearchPollCoordinator;
